import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, Check, X, Clock, AlertCircle, Users, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import api, { studentApi, classApi, schoolYearApi } from '../lib/api';

export default function AttendancePage() {
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [classes, setClasses] = useState([]);
  const [schoolYear, setSchoolYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, [selectedDate, selectedClass]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [studentsRes, classesRes, yearRes] = await Promise.all([
        studentApi.getAll({ is_active: true }),
        classApi.getAll({}),
        schoolYearApi.getActive()
      ]);
      
      let filteredStudents = studentsRes.data;
      if (selectedClass !== 'all') {
        filteredStudents = filteredStudents.filter(s => s.class_id === selectedClass);
      }
      
      // Get attendance for this date
      const attendanceRes = await api.get('/attendance', { params: { date: selectedDate } });
      
      setStudents(filteredStudents);
      setClasses(classesRes.data);
      setSchoolYear(yearRes.data);
      setAttendance(attendanceRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getStudentAttendance = (studentId) => {
    return attendance.find(a => a.student_id === studentId);
  };

  const handleMarkAttendance = async (studentId, status) => {
    setSaving(true);
    try {
      await api.post('/attendance', {
        student_id: studentId,
        school_year_id: schoolYear?.id || '',
        date: selectedDate,
        status: status,
        class_id: students.find(s => s.id === studentId)?.class_id || null
      });
      
      // Update local state
      const existingIndex = attendance.findIndex(a => a.student_id === studentId);
      if (existingIndex >= 0) {
        const updated = [...attendance];
        updated[existingIndex] = { ...updated[existingIndex], status };
        setAttendance(updated);
      } else {
        setAttendance([...attendance, { student_id: studentId, status, date: selectedDate }]);
      }
      
      toast.success('Présence enregistrée');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handlePrevDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  const getStatusInfo = (status) => {
    const statuses = {
      present: { label: 'Présent', class: 'bg-green-100 text-green-700 border-green-200', icon: Check },
      absent: { label: 'Absent', class: 'bg-red-100 text-red-700 border-red-200', icon: X },
      late: { label: 'Retard', class: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
      excused: { label: 'Excusé', class: 'bg-blue-100 text-blue-700 border-blue-200', icon: AlertCircle },
    };
    return statuses[status] || { label: 'Non marqué', class: 'bg-gray-100 text-gray-500', icon: null };
  };

  // Stats
  const stats = {
    total: students.length,
    present: attendance.filter(a => a.status === 'present').length,
    absent: attendance.filter(a => a.status === 'absent').length,
    late: attendance.filter(a => a.status === 'late').length,
    notMarked: students.length - attendance.filter(a => students.some(s => s.id === a.student_id)).length
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Suivi des Absences</h1>
          <p className="text-muted-foreground text-sm">Gérez la présence quotidienne des élèves</p>
        </div>
      </div>

      {/* Date Navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevDay}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-full">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">
                  {format(new Date(selectedDate), 'EEEE dd MMMM yyyy', { locale: fr })}
                </span>
              </div>
              <Button variant="outline" size="icon" onClick={handleNextDay}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" className="ml-2 rounded-full" onClick={handleToday}>
                Aujourd'hui
              </Button>
            </div>
            
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="w-full sm:w-48 rounded-full">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Toutes les classes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les classes</SelectItem>
                {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              <Users className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.present}</p>
              <p className="text-xs text-muted-foreground">Présents</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <X className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.absent}</p>
              <p className="text-xs text-muted-foreground">Absents</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.late}</p>
              <p className="text-xs text-muted-foreground">Retards</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.notMarked}</p>
              <p className="text-xs text-muted-foreground">Non marqués</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Students List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Liste des élèves</CardTitle>
          <CardDescription>Cliquez sur un statut pour marquer la présence</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">Aucun élève dans cette sélection</p>
            </div>
          ) : (
            <div className="space-y-2">
              {students.map((student) => {
                const studentAttendance = getStudentAttendance(student.id);
                const currentStatus = studentAttendance?.status;
                
                return (
                  <div key={student.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors" data-testid={`attendance-${student.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">
                          {student.first_name[0]}{student.last_name[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{student.first_name} {student.last_name}</p>
                        <p className="text-xs text-muted-foreground">{student.matricule}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {['present', 'absent', 'late', 'excused'].map((status) => {
                        const info = getStatusInfo(status);
                        const Icon = info.icon;
                        const isActive = currentStatus === status;
                        
                        return (
                          <Button
                            key={status}
                            variant="outline"
                            size="sm"
                            className={cn(
                              "rounded-full transition-all",
                              isActive && info.class,
                              !isActive && "opacity-50 hover:opacity-100"
                            )}
                            onClick={() => handleMarkAttendance(student.id, status)}
                            disabled={saving}
                          >
                            {Icon && <Icon className="w-4 h-4 mr-1" />}
                            <span className="hidden sm:inline">{info.label}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
